import { useState } from 'react';
import Reveal from './Reveal';

function SectionHeading({ eyebrow, title, accent }: { eyebrow: string; title: string; accent: string }) {
  return (
    <div className="text-center mb-20">
      <p className="eyebrow text-teal-400 text-sm md:text-base mb-4">{eyebrow}</p>
      <h2 className="text-3xl md:text-5xl font-mincho text-white">{title}</h2>
      <div className="w-16 h-[3px] bg-teal-400 mx-auto mt-8 rounded-full"></div>
    </div>
  );
}

export default function AboutSection() {
  const [showAddress, setShowAddress] = useState(false);

  return (
    <section id="about" className="py-24 md:py-32 bg-site-mid">
      <div className="max-w-5xl mx-auto px-4 md:px-6">

        {/* スタッフ紹介 */}
        <Reveal>
          <SectionHeading eyebrow="Our Team" title="スタッフ紹介" accent="" />
        </Reveal>

        <div className="flex flex-col md:flex-row gap-10 md:gap-16 items-start mb-24">
          <Reveal variant="fly" direction="left">
          <div className="w-full md:w-[340px] flex-shrink-0">
            <img
              src="/images/brand/founder.jpg"
              alt="石川泰三 代表"
              className="w-full h-[420px] md:h-[460px] object-cover object-top rounded-xl"
            />
          </div>
          </Reveal>
          <div className="flex-1 pt-2">
            <div className="mb-6">
              <p className="text-xl md:text-2xl font-mincho text-white mb-1">石川泰三</p>
              <p className="text-sm text-teal-300">代表 プロデューサー、MAエンジニア</p>
            </div>
            <div className="space-y-4 text-zinc-300 text-sm md:text-base leading-relaxed">
              <p>
                1976年大阪生まれ。音響業界歴28年。
              </p>
              <p>
                数多くのMAスタジオや音楽制作会社で企業VP、CM、映画、展示会などの音響制作を手がけた後、2025年に独立起業。大阪・関西万博では福井パビリオンの音響演出を担当。
              </p>
              <p>
                「人をWakuWakuさせる音を一緒に創りたい」を理念に、映像や空間の魅力を最大限に引き出す音のデザインで、聴く人・観る人の心を動かす瞬間をつくり続けています。
              </p>
            </div>
          </div>
        </div>

        {/* 会社概要 */}
        <Reveal>
          <SectionHeading eyebrow="Company" title="会社概要" accent="" />
        </Reveal>

        <div className="border-t border-white/10 mb-24">
          {[
            ['会社名', '合同会社WakuWaku', '（法人番号：6120903005953）'],
            ['所在地', '〒562-0001 大阪府箕面市小野原東二丁目3番22号', ''],
            ['設立', '2025年11月11日', ''],
            ['代表者', '石川 泰三（いしかわ たいぞう）', ''],
            ['資本金', '200万円', ''],
            ['事業内容', '映像・CM・映像音響のMA（整音）制作 / 5.1chサラウンド制作 / AI音楽制作 / ナレーション・口録音 / 出張編集', ''],
            ['取引金融機関', '池田泉州銀行 小野原支店 / 三井住友銀行 トランクNORTH支店', ''],
          ].map(([label, value, note], i) => (
            <div key={i} className="flex flex-col sm:flex-row border-b border-white/10">
              <div className="sm:w-40 md:w-48 py-4 px-4 md:px-6 bg-white/[0.03] text-sm font-semibold text-teal-300 flex-shrink-0">
                {label}
              </div>
              <div className="flex-1 py-4 px-4 md:px-6 text-sm md:text-base text-zinc-200 leading-relaxed">
                {label === '所在地' ? (
                  <button
                    type="button"
                    onClick={() => setShowAddress((prev) => !prev)}
                    aria-label={showAddress ? '所在地を隠す' : '所在地を表示'}
                    className="text-left cursor-pointer"
                  >
                    {showAddress ? (
                      <span className="text-zinc-200">{value}</span>
                    ) : (
                      <span className="text-zinc-500 tracking-widest select-none">••••••••••••••••••••••••••</span>
                    )}
                  </button>
                ) : (
                  <>
                    {value}
                    {note && <span className="text-zinc-500 text-sm ml-2">{note}</span>}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* 沿革 */}
        <Reveal>
          <SectionHeading eyebrow="History" title="沿革" accent="" />
        </Reveal>

        <div className="border-t border-white/10 mb-20">
          {[
            ['2026年9月28日', 'ワクワクスタジオ完成'],
            ['2025年11月', '大阪府箕面市に合同会社WakuWakuを設立'],
            ['2025年4月〜10月', '大阪・関西万博 福井パビリオン音響演出担当'],
          ].map(([date, content], i) => (
            <div key={i} className="flex flex-col sm:flex-row border-b border-white/10">
              <div className="sm:w-36 md:w-44 py-4 px-4 md:px-6 text-sm font-semibold text-teal-300 flex-shrink-0">
                {date}
              </div>
              <div className="flex-1 py-4 px-4 md:px-6 text-sm md:text-base text-zinc-200">
                {content}
              </div>
            </div>
          ))}
        </div>

        {/* 事業理念 */}
        <Reveal>
        <div className="bg-teal-500/10 border border-teal-400/20 text-white p-8 md:p-14 rounded-xl">
          <p className="eyebrow text-teal-300 text-center text-sm mb-4">Our Philosophy</p>
          <h3 className="text-2xl md:text-3xl font-mincho mb-5 text-center">事業理念</h3>
          <p className="text-lg md:text-2xl font-mincho mb-4 text-center text-teal-200">
            「人をWakuWakuさせる音を一緒に創りたい」
          </p>
          <p className="text-zinc-300 text-sm md:text-base leading-relaxed text-center max-w-2xl mx-auto">
            音は、心の奥に届く"感情のスイッチ"。私たちは、映像や空間の魅力を最大限に引き出す「音のデザイン」を通して、聴く人・観る人の心を動かす夢のある瞬間をつくります。
          </p>
        </div>
        </Reveal>

      </div>
    </section>
  );
}
